// Anonymous visitor identity + client-side analytics events, shared by the
// app, the landing page and the resources pages (same origin, same ID).

import type { ClientAnalyticsEventName } from '../shared/types';

const ANON_ID_KEY = 'vimsensei:anon-id';

export function getOrCreateAnonId(): string | null {
  try {
    let id = localStorage.getItem(ANON_ID_KEY);
    if (!id) {
      const uuid =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 14)}`;
      id = uuid.replace(/-/g, '').slice(0, 32);
      localStorage.setItem(ANON_ID_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

/**
 * Where this page view came from: UTM params, plus the referrer split into an
 * external host or an internal path (e.g. a resources article).
 */
export function acquisitionProps(): Record<string, string> {
  const props: Record<string, string> = {};

  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const utmCampaign = params.get('utm_campaign');
  if (utmSource) props.utmSource = utmSource;
  if (utmMedium) props.utmMedium = utmMedium;
  if (utmCampaign) props.utmCampaign = utmCampaign;

  if (document.referrer) {
    try {
      const referrer = new URL(document.referrer);
      if (referrer.host === window.location.host) {
        props.fromPath = referrer.pathname;
      } else {
        props.referrerHost = referrer.host;
      }
    } catch {
      // Malformed referrer — treat as direct.
    }
  }

  return props;
}

export function trackEvent(
  name: ClientAnalyticsEventName,
  props: Record<string, string | boolean> = {},
  token?: string | null,
): void {
  const anonId = getOrCreateAnonId();
  if (!anonId) return;

  fetch('/api/track', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: JSON.stringify({ name, anonId, props }),
    keepalive: true,
  }).catch(() => {
    // Fire-and-forget; analytics failures must not affect the user.
  });
}
